import React, {ChangeEvent, FC, Suspense, useState} from 'react'
import {useTranslation} from 'react-i18next'

import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'

import PageLoader from '@progyny/components/PageLoader'

type Props = {
  values: {
    password: string
    currentPassword?: string
    confirmPassword?: string
  }
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  errors?: any
  label?: string
  showStrength?: boolean
  hiddenLabel?: boolean
  placeholder?: string
  focusInfo?: boolean
  noInfo?: boolean
} & TextFieldProps

const PasswordStrengthBar = React.lazy(
  () => import('react-password-strength-bar')
)

const PasswordField: FC<Props> = (props) => {
  const {
    values,
    onChange,
    errors,
    label,
    placeholder,
    showStrength,
    hiddenLabel = false,
    focusInfo = false,
    noInfo = false,
    ...rest
  } = props
  const {t} = useTranslation()
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  })

  const [showInfo, setShowInfo] = useState(!focusInfo)

  const passwordLabel = label || t('password_field_password')

  const {password, confirmPassword, currentPassword} = values

  const passwordAdornment = (name: string) => (
    <InputAdornment position='end'>
      <IconButton
        color={'default'}
        size='small'
        aria-label={t('password_toggle_visibility')}
        onClick={() =>
          setShowPassword({
            ...showPassword,
            [name]: !showPassword[name as keyof typeof showPassword],
          })
        }
        edge='end'
      >
        {showPassword[name as keyof typeof showPassword] ? (
          <VisibilityOffOutlinedIcon />
        ) : (
          <VisibilityOutlinedIcon />
        )}
      </IconButton>
    </InputAdornment>
  )

  return (
    <>
      {currentPassword !== undefined && (
        <Box mb={3}>
          <TextField
            name='currentPassword'
            type='password'
            onChange={onChange}
            placeholder={t('password_field_current_password')}
            label={t('password_field_current_password')}
            value={currentPassword}
            variant='outlined'
            size='small'
            error={errors?.currentPassword.hasError}
            helperText={t(errors?.currentPassword.message)}
            fullWidth
            hiddenLabel={hiddenLabel}
            inputProps={{
              autoComplete: 'current-password',
            }}
            {...rest}
          />
        </Box>
      )}
      <Box>
        <TextField
          fullWidth
          name='password'
          variant='outlined'
          size='small'
          type={showPassword.password ? 'text' : 'password'}
          onChange={onChange}
          placeholder={placeholder || passwordLabel}
          label={hiddenLabel ? null : passwordLabel}
          value={password}
          hiddenLabel={hiddenLabel}
          error={errors?.password.hasError}
          helperText={t(errors?.password.message)}
          autoComplete={'new-password'}
          onFocus={() => {
            setShowInfo(true)
          }}
          InputProps={{
            endAdornment: passwordAdornment('password'),
          }}
          {...rest}
        />
        {showStrength && password && (
          <Suspense fallback={<PageLoader position={'center'} />}>
            <Box mt={1}>
              <PasswordStrengthBar password={password} />
            </Box>
          </Suspense>
        )}
      </Box>
      {confirmPassword !== undefined && (
        <Box mt={3}>
          <TextField
            name='confirmPassword'
            onChange={onChange}
            placeholder={t('password_field_repeat_new_password')}
            label={!hiddenLabel && t('password_field_repeat_new_password')}
            type={showPassword.confirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            variant='outlined'
            size='small'
            fullWidth
            hiddenLabel={hiddenLabel}
            error={errors?.confirmPassword.hasError}
            helperText={t(errors?.confirmPassword.message)}
            InputProps={{
              endAdornment: passwordAdornment('confirmPassword'),
            }}
            {...rest}
          />
        </Box>
      )}
      {showInfo && !noInfo && (
        <Box mt={3} mb={6}>
          <Typography variant='body2' gutterBottom>
            {t('password_field_your_password_needs_to_contain')}:
          </Typography>
          <Typography variant='body2' component='ul'>
            <li>{t('password_field_minimum_of_twelve_12_characters')}</li>
            <li>{t('password_field_not_only_numeric_characters')}</li>
            <li>{t('password_field_your_password_will_be_checked')}</li>
          </Typography>
        </Box>
      )}
    </>
  )
}

export const numericRegex = /^[0-9]+$/

export {PasswordField}
